import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Link2, LinkIcon, ChevronLeft, ChevronRight, Star, ExternalLink, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import Cookies from 'js-cookie';
import axios from 'axios';
import ItemDetailsModal from '../../../../../components/ItemDetailsModal';
import { updateLink, deleteLink } from '../../../../../redux/actions/search.action';
import { configureKeyboardNavigation } from '../../../../../redux/actions/global.action';
import { BACKEND_API_URL } from '../../../../../utils/api';

const LinkResults = ({
    links,
    totalCount,
    startIndex,
    endIndex,
    page,
    pageSize,
    onPageChange,
    headerOnly = false,
    contentOnly = false
}) => {
    const dispatch = useDispatch();
    const totalPages = Math.ceil(totalCount / pageSize);

    // Handler for link name/star/tags updates
    const handleUpdateLink = (id, name, status = null, star_level = null, tags = null) => {
        const updates = {};
        if (status !== null) updates.status = status;
        else if (star_level !== null) updates.star_level = star_level;
        else if (tags !== null) updates.tags = tags;
        else updates.name = name;
        
        dispatch(updateLink(id, updates));
    };

    // Handler for link deletion
    const handleDeleteLink = (id) => {
        dispatch(deleteLink(id));
    };
    
    // Header Component
    const Header = () => (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <div className="flex items-center justify-center h-6 w-6 rounded-md bg-primary/15 dark:bg-primary/20">
                    <Link2 className="h-3.5 w-3.5 text-primary" />
                </div>
                <span className="font-semibold text-sm">Links</span>
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-mono border-primary/30 text-primary bg-primary/10 dark:bg-primary/15">
                    {totalCount}
                </Badge>
            </div>
            
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">
                        {startIndex + 1}-{endIndex}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-primary/15 dark:hover:bg-primary/20"
                        onClick={(e) => onPageChange(e, page - 1)}
                        disabled={page === 1}
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-[10px] font-medium min-w-[40px] text-center tabular-nums">
                        {page}/{totalPages}
                    </span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 hover:bg-primary/15 dark:hover:bg-primary/20"
                        onClick={(e) => onPageChange(e, page + 1)}
                        disabled={page === totalPages}
                    >
                        <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                </div>
            )}
            
            {totalPages <= 1 && totalCount > 0 && (
                <span className="text-[10px] text-muted-foreground font-mono">
                    {startIndex + 1}-{endIndex} of {totalCount}
                </span>
            )}
        </div>
    );

    // Content Component
    const Content = () => (
        <>
            {links.length > 0 ? (
                <div className="divide-y divide-border/50">
                    {links.map(link => (
                        <CompactLinkRow 
                            key={link.id} 
                            link={link}
                            onUpdate={handleUpdateLink}
                            onDelete={handleDeleteLink}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                    <LinkIcon className="h-10 w-10 mb-2 opacity-20" />
                    <p className="text-xs font-medium">No links found</p>
                    <p className="text-[10px] mt-0.5 opacity-60">Try adjusting your filters</p>
                </div>
            )}
        </>
    );

    if (headerOnly) {
        return <Header />;
    }

    if (contentOnly) {
        return <Content />;
    }

    // Full render (for backwards compatibility)
    return (
        <div className="w-full">
            <Header />
            <Content />
        </div>
    );
};

// Compact link row component for search results
const CompactLinkRow = ({ link, onUpdate, onDelete }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [linkData, setLinkData] = useState(null);

    const getStarColor = (level) => {
        if (level === 3) return "text-red-500";
        if (level === 2) return "text-orange-500";
        if (level === 1) return "text-primary";
        return "text-muted-foreground/30";
    };

    const getDomain = (url) => {
        try {
            const hostname = new URL(url).hostname;
            return hostname.replace('www.', '');
        } catch {
            return null;
        }
    };

    const assignee = link.employees?.[0];
    const extraAssignees = (link.employees?.length || 0) - 1;
    const domain = getDomain(link.link);

    // Fetch link details when modal opens
    const fetchLinkDetails = useCallback(async () => {
        try {
            const accessToken = Cookies.get("access_token");
            const response = await axios.get(`${BACKEND_API_URL}/api/link/${link.id}`, {
                headers: {
                    'Authorization': `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            setLinkData(response.data);
        } catch (error) {
            console.error('Error fetching link details:', error);
        }
    }, [link.id]);

    // Open modal handler
    const handleOpen = useCallback(() => {
        setIsOpen(true);
        dispatch(configureKeyboardNavigation(false));
        fetchLinkDetails();
    }, [dispatch, fetchLinkDetails]);

    // Close modal handler
    const handleClose = useCallback(() => {
        setIsOpen(false);
        dispatch(configureKeyboardNavigation(true));
    }, [dispatch]);

    // Handle click events - matching canvas LinkItem behavior:
    // Single click: open link URL in new tab
    // Double-click: open link URL in new tab
    // Right-click: open modal
    const handleClick = (e) => {
        if (e.type === 'contextmenu') {
            // Right-click: open modal
            e.preventDefault();
            handleOpen();
        } else {
            // Single-click and double-click: open link URL in new tab
            window.open(link.link, '_blank');
        }
    };

    // Handler for link updates from modal
    const handleUpdateLink = (id, name, status = null, starLevel = null, isArchived = null, tags = null) => {
        if (onUpdate) {
            onUpdate(id, name, status, starLevel, tags);
        }
    };

    return (
        <>
            <div 
                onClick={handleClick}
                onContextMenu={handleClick}
                className={cn(
                    "group flex items-center gap-2 px-2.5 py-1.5 cursor-pointer",
                    "hover:bg-primary/10 dark:hover:bg-primary/15 transition-colors duration-150",
                    link.is_archived && "opacity-60"
                )}
            >
                {/* Star indicator */}
                <div className="shrink-0 w-4">
                    {link.star_level > 0 && (
                        <Star className={cn("h-3 w-3 fill-current", getStarColor(link.star_level))} />
                    )}
                </div>

                {/* Link icon */}
                <div className="shrink-0 h-5 w-5 rounded bg-primary/15 dark:bg-primary/20 flex items-center justify-center">
                    <Globe className="h-3 w-3 text-primary" />
                </div>

                {/* Name and path */}
                <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-center gap-1.5">
                        <span className={cn(
                            "text-xs font-medium truncate",
                            link.is_archived ? "text-muted-foreground" : "text-foreground"
                        )}>
                            {link.name || link.link}
                        </span>
                        {domain && (
                            <span className="text-[8px] px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0 hidden sm:inline">
                                {domain.length > 20 ? domain.slice(0, 18) + '…' : domain}
                            </span>
                        )}
                    </div>
                    {link.path_string && (
                        <span className="text-[9px] text-muted-foreground/70 truncate leading-tight" title={link.path_string}>
                            {link.path_string}
                        </span>
                    )}
                </div>

                {/* Tags - show max 2 */}
                {link.tags?.length > 0 && (
                    <div className="hidden sm:flex items-center gap-0.5 shrink-0">
                        {link.tags.slice(0, 2).map((tag, i) => (
                            <span key={i} className="inline-flex items-center px-1 py-0.5 rounded text-[8px] bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 truncate max-w-[50px]">
                                {tag.name}
                            </span>
                        ))}
                        {link.tags.length > 2 && (
                            <span className="text-[8px] text-muted-foreground">+{link.tags.length - 2}</span>
                        )}
                    </div>
                )}

                {/* Assignee */}
                {assignee && (
                    <div className="shrink-0 flex items-center gap-1" title={`${assignee.first_name} ${assignee.last_name}`}>
                        <div 
                            className="h-5 w-5 rounded-md flex items-center justify-center text-[8px] font-semibold text-white"
                            style={{ backgroundColor: `hsl(${Math.abs([...`${assignee.first_name}${assignee.last_name}`].reduce((h, c) => c.charCodeAt(0) + ((h << 5) - h), 0) % 360)}, 55%, 50%)` }}
                        >
                            {assignee.first_name.slice(0,1)}{assignee.last_name.slice(0,1)}
                        </div>
                        {extraAssignees > 0 && (
                            <span className="text-[8px] text-muted-foreground">+{extraAssignees}</span>
                        )}
                    </div>
                )}

                {/* Open link indicator */}
                <ExternalLink className="h-3 w-3 text-muted-foreground/0 group-hover:text-primary/70 transition-colors shrink-0" />
            </div>

            {/* Item Details Modal */}
            {linkData && (
                <ItemDetailsModal
                    isOpen={isOpen}
                    handleClose={handleClose}
                    depth={-1}
                    itemData={link}
                    itemInfo={linkData}
                    updateNameHandler={handleUpdateLink}
                    setItemData={setLinkData}
                    on_delete={onDelete}
                    href={link.link}
                    itemType="link"
                    fetchItemDetails={fetchLinkDetails}
                />
            )}
        </>
    );
};

export default LinkResults;
